import { Box, Link, Tooltip, Typography, styled } from "@mui/material"
import type { TypographyProps } from "@mui/material/Typography"
import { motion } from "motion/react"
import { useState } from "react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router"

import { pxToRem } from "@sap/theme/typography"

const Label = styled("div")({
  position: "absolute",
  top: 11,
  left: 0,
  fontSize: "0.75rem",
  backgroundColor: "#E1E2E3",
  maxWidth: "calc(100% - 16px)",
  padding: "5px 10px",
  zIndex: 5,
})

const OuterImg = styled("div")({
  position: "relative",
  width: "100%",
  height: "0",
  paddingTop: "125%",
})

const InnerImg = styled("div")(() => ({
  position: "absolute",
  textAlign: "center",
  alignItems: "center",
  justifyContent: "center",
  padding: "max(10%,15px)",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  backgroundColor: "#f5f5f5", // theme.palette.background.neutral,
  border: `1px solid #f0f0f0`,
}))

const Img = styled(motion.img)({
  width: "100%",
  height: "82%",
  objectFit: "contain",
  alignSelf: "center",
  display: "inline-block",
  flexShrink: 0,
  mixBlendMode: "darken",
})

const Container = styled("div")({})

const ImageFrame = styled("div")({ position: "relative" })

const ImageAction = styled("div")({
  position: "absolute",
  bottom: 10,
  right: 10,
  zIndex: 6,
})

const DescriptionWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact: boolean }>(({ compact }) => ({
  marginTop: compact ? "10px" : "2rem",
  position: "relative",
}))

const CategoryText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact: boolean }>(({ compact }) => ({
  position: compact ? "static" : "absolute",
  top: "-20px",
  left: 0,
}))

const TitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "compact",
})<TypographyProps & { compact: boolean }>(({ compact }) => ({
  fontSize: "1.125rem !important",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: "2",
  WebkitBoxOrient: "vertical",
  minHeight: compact ? 0 : "3rem",
  ...(compact && { lineHeight: 1.35 }),
}))

const SatellietOriginalImg = styled(Box)({
  position: "absolute",
  top: 15,
  right: 15,
  fontSize: "0.75rem",
  maxWidth: "calc(100% - 16px)",
  width: pxToRem(30),
  height: pxToRem(30),
  zIndex: 5,
}) as typeof Box

const SimilarityBadge = styled("span")({
  position: "absolute",
  top: 11,
  left: 11,
  padding: "3px 8px",
  borderRadius: 5,
  backgroundColor: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  fontSize: "0.75rem",
  fontWeight: 600,
  zIndex: 5,
})

function ProductCard({
  id,
  title,
  imgUrl,
  brandNew = false,
  category = "",
  satellietOriginals = false,
  similarityLabel,
  imageAction,
  compact = false,
  onClick = () => {},
}: {
  id: string
  title: string
  imgUrl: string
  brandNew?: boolean
  category?: string
  satellietOriginals?: boolean
  similarityLabel?: string
  imageAction?: ReactNode
  compact?: boolean
  onClick?: () => void
}): JSX.Element {
  const { t } = useTranslation()

  const [imgError, setImgError] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)

  const handleImageLoad = () => {
    setImgLoaded(true)
  }

  return (
    <Container>
      <ImageFrame>
        <Link
          to={`/item/${encodeURIComponent(id)}`}
          component={RouterLink}
          color="inherit"
          underline="none"
          sx={{ display: "block" }}
          onClick={onClick}
          aria-label={title}
        >
          <OuterImg>
            {brandNew && (
              <Label
                sx={{
                  top: {
                    xs: 7,
                    sm: 11,
                  },
                  ...(similarityLabel && { top: 45 }),
                }}
              >
                BRAND NEW
              </Label>
            )}
            {satellietOriginals && (
              <Tooltip title={t("SatellietOriginals")}>
                <SatellietOriginalImg
                  component="img"
                  src="/assets/images/Icoon_Satelliet_Originals.png"
                  alt={t("SatellietOriginals")}
                  sx={{
                    top: {
                      xs: 7,
                      sm: 15,
                    },
                    right: {
                      xs: 7,
                      sm: 15,
                    },
                  }}
                />
              </Tooltip>
            )}
            <InnerImg>
              {imgError ? (
                <Img
                  src={"/assets/images/Satelliet_placeholder.png"}
                  alt=""
                  initial={{ opacity: 0.2 }}
                  animate={{ opacity: imgLoaded ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                  onLoad={handleImageLoad}
                />
              ) : (
                <Img
                  src={imgUrl || "/assets/images/Satelliet_placeholder.png"}
                  alt=""
                  onError={() => {
                    setImgError(true)
                  }}
                  initial={{ opacity: 0.2 }}
                  animate={{ opacity: imgLoaded ? 1 : 0 }}
                  transition={{ duration: 0.2 }}
                  onLoad={handleImageLoad}
                />
              )}
            </InnerImg>
            {similarityLabel && (
              <SimilarityBadge aria-label={similarityLabel} title={similarityLabel}>
                {similarityLabel}
              </SimilarityBadge>
            )}
          </OuterImg>
        </Link>
        {imageAction && <ImageAction>{imageAction}</ImageAction>}
      </ImageFrame>
      <DescriptionWrapper compact={compact}>
        <Link
          to={`/item/${encodeURIComponent(id)}`}
          component={RouterLink}
          color="inherit"
          underline="none"
          sx={{ display: "block" }}
          onClick={onClick}
        >
          <CategoryText compact={compact} variant="body2">
            {category}
          </CategoryText>
          <TitleText compact={compact} variant="h6" component="h2">
            {title}
          </TitleText>
        </Link>
      </DescriptionWrapper>
    </Container>
  )
}

export default ProductCard
